import { useEffect, useState } from 'react';
import { buildObfuscatedKey, deobfuscatePayload, obfuscatePayload } from '../utils/cacheObfuscation.js';
import { getListingHomeId, getListingKey } from './listingDetailsApi.js';
import { track } from '../analytics/events.js';
import { edgeSources } from './edgePayloads.js';

//const DEFAULT_ENDPOINT = 'https://omradeskollen.pythonanywhere.com/listings/active';
//const DEFAULT_ENDPOINT = 'http://127.0.0.1:5000/listings/active';
const DEFAULT_ENDPOINT = 'https://api.xn--omrdeskollen-vcb.nu/listings/active/regso/batch';
const LISTINGS_CACHE_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000;
const LISTINGS_CACHE_DB = 'active-listings-cache-db-v6';
const LISTINGS_CACHE_STORE = 'listings';
const DAY_MS = 24 * 60 * 60 * 1000;
const AREA_KEY_PATTERN = /(area|sqm|m2|kvm|bo)/i;
const PLOT_KEY_PATTERN = /(plot|tomt|land)/i;
const LISTING_AREA_DEBUG = false;
const REGSO_IDS_FILE_URL = '/regsoId.txt';
const STREAM_FLUSH_REGSO_INTERVAL = 18;
const MAX_PARALLEL_REGSO_REQUESTS = 6;
const REGSO_BATCH_SIZE = 5;
const LISTINGS_NORMALIZE_CHUNK_SIZE = 1280;
const LISTINGS_FLUSH_CHUNK_INTERVAL = 8;
const LISTING_TIMING_LOG = true;
let regsoIdsPromise = null;

const nowMs = () => (
    typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
);

const formatDuration = (value) => `${Number(value).toFixed(1)}ms`;

const logListingTiming = (stage, details = {}) => {
    if (!LISTING_TIMING_LOG) return;
};

const yieldToMainThread = async () => new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 60 });
        return;
    }
    setTimeout(resolve, 0);
});



const parseNumber = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'object') {
        const nestedKeys = ['value', 'amount', 'area', 'sqm', 'm2', 'size', 'livingArea', 'living_area', 'boarea', 'text', 'label'];
        for (const key of nestedKeys) {
            if (value[key] != null) {
                const parsedNested = parseNumber(value[key]);
                if (parsedNested != null) return parsedNested;
            }
        }
        return null;
    }
    const normalized = String(value).replace(/\s+/g, '').replace(',', '.');
    const digits = normalized.match(/-?\d+(\.\d+)?/);
    return digits ? Number(digits[0]) : null;
};

const parseAreaFromText = (value) => {
    if (value == null) return null;
    const text = String(value);
    const match = text.match(/(\d+(?:[.,]\d+)?)\s*(m²|kvm|m2)/i);
    if (!match) return null;
    const parsed = Number(match[1].replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeAreaNumber = (value) => {
    if (!Number.isFinite(value)) return null;
    // Guard against area ids/codes being interpreted as square meters.
    if (value < 8 || value > 5000) return null;
    return value;
};

const getAreaCandidate = (item) => (
    item.area
    ?? item.livingM2
    ?? item.living_m2
    ?? item.living_area
    ?? item.livingArea
    ?? item.boarea
    ?? item.boa
    ?? item.bo_area
    ?? item.area_m2
    ?? item.areaM2
    ?? item.size_m2
    ?? item.sizeM2
    ?? item.size
    ?? item.sqm
    ?? item.square_meters
    ?? item.squareMeters
    ?? null
);

const toAreaDisplay = (value) => {
    if (value == null) return '';
    if (typeof value === 'object') {
        const textValue = value.text ?? value.label ?? value.value ?? value.amount ?? value.area ?? value.sqm ?? value.m2 ?? value.size;
        return textValue != null ? String(textValue) : '';
    }
    return String(value);
};

const getAreaValueFromItem = (item) => {
    const direct = parseNumber(getAreaCandidate(item));
    const normalizedDirect = normalizeAreaNumber(direct);
    if (normalizedDirect != null) return normalizedDirect;

    const stack = [{ node: item, depth: 0 }];
    while (stack.length) {
        const current = stack.pop();
        const { node, depth } = current;
        if (!node || typeof node !== 'object' || depth > 3) continue;
        Object.entries(node).forEach(([key, value]) => {
            // Plot size ("plot_area", "tomt…") is not living area.
            if (AREA_KEY_PATTERN.test(key) && !PLOT_KEY_PATTERN.test(key)) {
                const parsed = normalizeAreaNumber(parseNumber(value));
                if (parsed != null) {
                    stack.length = 0;
                    current.found = parsed;
                    return;
                }
                const fromText = normalizeAreaNumber(parseAreaFromText(value));
                if (fromText != null) {
                    stack.length = 0;
                    current.found = fromText;
                    return;
                }
            }
            if (value && typeof value === 'object') {
                stack.push({ node: value, depth: depth + 1 });
            }
        });
        if (current.found != null) return current.found;
    }

    return normalizeAreaNumber(parseAreaFromText([item.title, item.subtitle, item.description].filter(Boolean).join(' ')));
};

const parsePriceValue = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const cleaned = String(value).replace(/[^\d]/g, '');
    return cleaned ? Number(cleaned) : null;
};

const parseFeeValue = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : null;
    if (typeof value === 'object') {
        const nestedKeys = ['value', 'amount', 'fee', 'avgift', 'monthlyFee', 'monthly_fee', 'text', 'label'];
        for (const key of nestedKeys) {
            if (value[key] != null) {
                const parsedNested = parseFeeValue(value[key]);
                if (parsedNested != null) return parsedNested;
            }
        }
        return null;
    }
    const parsed = parsePriceValue(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const getFeeCandidate = (item) => (
    item?.fee
    ?? item?.avgift
    ?? item?.monthly_fee
    ?? item?.monthlyFee
    ?? item?.monthly_cost
    ?? item?.monthlyCost
    ?? item?.charge
    ?? null
);

const normalizeFeeText = (value) => {
    if (value == null) return '';
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return '';
        return `${Math.round(value).toLocaleString('sv-SE').replace(/\s+/g, ' ')} kr/mån`;
    }
    if (typeof value === 'object') {
        const nested = value.text ?? value.label ?? value.value ?? value.amount ?? value.fee ?? value.avgift ?? value.monthlyFee ?? value.monthly_fee;
        return nested != null ? normalizeFeeText(nested) : '';
    }
    const text = String(value).trim();
    if (!text) return '';
    const parsed = parseFeeValue(text);
    if (!Number.isFinite(parsed)) return text;
    if (/kr|sek/i.test(text)) {
        return text.replace(/\s+/g, ' ').trim();
    }
    return `${parsed.toLocaleString('sv-SE').replace(/\s+/g, ' ')} kr/mån`;
};

// The API's own type codes; 'house++' is par/radhus.
const API_TYPES = { apartment: 'apartment', house: 'house', 'house++': 'rowhouse', rowhouse: 'rowhouse',
    fritidshus: 'fritidshus' };

const normalizeType = (item) => {
    const apiType = API_TYPES[String(item?.type || '').trim().toLowerCase()];
    if (apiType) return apiType;
    const primaryText = [
        item?.type,
        item?.property_type,
        item?.housing_type,
        item?.object_type,
        item?.estate_type,
        item?.category,
        item?.housingCategory,
        item?.propertyType,
        item?.title,
        item?.subtitle,
        item?.address,
        item?.description,
        item?.tags,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    const fullText = `${primaryText} ${JSON.stringify(item || {}).toLowerCase()}`;

    if (fullText.includes('apartment') || fullText.includes('lägenhet') || fullText.includes('lagenhet')) {
        return 'apartment';
    }
    if (/radhus|parhus|rad\/parhus|rad-?hus|kedjehus|townhouse|\brow\b|\browhouse\b/.test(fullText)) {
        return 'rowhouse';
    }
    if (fullText.includes('house') || fullText.includes('villa') || fullText.includes('hus')) {
        return 'house';
    }
    return 'house';
};

const parseLatLongString = (value) => {
    if (!value) return null;
    const parts = String(value).split(',');
    if (parts.length < 2) return null;
    const lat = parseNumber(parts[0]);
    const lng = parseNumber(parts[1]);
    if (lat == null || lng == null) return null;
    return { lat, lng };
};

const toTrimmedText = (value) => String(value || '').trim();

const normalizeListingPath = (item) => (
    toTrimmedText(item?.listingUrl || item?.listing_url || item?.path || item?.listing_path || item?.url || item?.link || '')
);

const normalizeMediaArray = (value, fallback = null) => {
    const source = Array.isArray(value)
        ? value
        : (fallback != null ? [fallback] : []);
    return source
        .map((entry) => toTrimmedText(entry))
        .filter(Boolean);
};

const normalizeBoolean = (value, defaultValue = false) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1') return true;
        if (normalized === 'false' || normalized === '0') return false;
    }
    return defaultValue;
};

const getLatLng = (item) => {
    const lat = item.lat ?? item.latitude ?? null;
    const lng = item.lng ?? item.longitude ?? null;
    if (lat != null && lng != null) {
        return { lat, lng };
    }
    return parseLatLongString(item.latlong) || { lat: null, lng: null };
};

const parseYmdDate = (value) => {
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
    ) {
        return null;
    }
    return parsed;
};

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatRelativeListingDate = (value) => {
    const parsed = parseYmdDate(value);
    if (!parsed) return null;

    const today = getStartOfDay(new Date());
    const listingDate = getStartOfDay(parsed);
    const diffDays = Math.floor((today.getTime() - listingDate.getTime()) / DAY_MS);

    if (!Number.isFinite(diffDays) || diffDays <= 0) {
        return 'inkommet idag';
    }
    if (diffDays === 1) {
        return 'inkommet igår';
    }
    if (diffDays >= 365) {
        const years = Math.floor(diffDays / 365);
        return years === 1 ? '1 år sedan' : `${years} år sedan`;
    }
    if (diffDays >= 30) {
        const months = Math.floor(diffDays / 30);
        return months === 1 ? '1 månad sedan' : `${months} månader sedan`;
    }
    return `${diffDays} dagar sedan`;
};

const normalizeDaysLabel = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        if (value <= 0) return 'inkommet idag';
        if (value === 1) return 'inkommet igår';
        if (value >= 365) {
            const years = Math.floor(value / 365);
            return years === 1 ? '1 år sedan' : `${years} år sedan`;
        }
        if (value >= 30) {
            const months = Math.floor(value / 30);
            return months === 1 ? '1 månad sedan' : `${months} månader sedan`;
        }
        return `${Math.floor(value)} dagar sedan`;
    }
    const relative = formatRelativeListingDate(value);
    if (relative) return relative;
    return value || '';
};

const extractRegsoCodeFromListingId = (listingId) => {
    const normalized = String(listingId || '').trim();
    if (!normalized) return '';
    const match = normalized.match(/^listing:([^|]+)\|/);
    return match?.[1] ? String(match[1]).trim() : '';
};

const resolveRegsoCode = (item) => {
    const direct = String(
        item?.regso_id
        || item?.regsoId
        || item?.regsoCode
        || item?.regsokod
        || item?.regso
        || '',
    ).trim();
    if (direct) return direct;
    return extractRegsoCodeFromListingId(item?.id);
};

const normalizeCachedListings = (cachedListings) => (
    Array.isArray(cachedListings)
        ? cachedListings.map((listing) => {
            const regsoCode = resolveRegsoCode(listing) || null;
            const feeCandidate = getFeeCandidate(listing);
            const homeId = getListingHomeId(listing);
            const listingUrl = normalizeListingPath(listing);
            return ({
                ...listing,
                homeId,
                listingUrl,
                fullData: normalizeBoolean(listing?.fullData ?? listing?.full_data, false),
                regsoId: regsoCode,
                regsoCode,
                regso: regsoCode,
                days: normalizeDaysLabel(listing?.days || listing?.date || ''),
                areaValue: (() => {
                    const cachedArea = parseNumber(listing?.areaValue ?? listing?.area ?? listing?.living_area ?? listing?.livingArea);
                    const normalizedCachedArea = normalizeAreaNumber(cachedArea);
                    if (normalizedCachedArea != null) return normalizedCachedArea;
                    return normalizeAreaNumber(parseAreaFromText([listing?.title, listing?.subtitle, listing?.description].filter(Boolean).join(' ')));
                })(),
                fee: normalizeFeeText(listing?.fee ?? listing?.avgift ?? feeCandidate),
                feeValue: parseFeeValue(listing?.feeValue ?? feeCandidate),
                images: normalizeMediaArray(listing?.images, listing?.image),
                planritning: normalizeMediaArray(listing?.planritning, listing?.floorplan),
                realtorUrl: toTrimmedText(listing?.realtorUrl ?? listing?.realtor_url ?? ''),
            });
        })
        : []
);

const buildListingId = (item, index, idScope = '') => {
    const explicitId = [
        item?.id,
        // "bostad/123": the number alone isn't unique (home ids and ad ids overlap).
        getListingKey(item),
        item?.hashedKey,
        item?.listingId,
        item?.listing_id,
        item?.objectId,
        item?.object_id,
        item?.uuid,
        item?.guid,
        item?.url,
        item?.link,
    ]
        .map((value) => (value == null ? '' : String(value).trim()))
        .find(Boolean);
    if (explicitId) return explicitId;

    const latLng = getLatLng(item);
    const regso = String(item?.regso_id || item?.regsoId || item?.regsoCode || item?.regsokod || item?.regso || '').trim();
    const address = String(item?.address || item?.title || item?.subtitle || '').trim().toLowerCase();
    const price = parsePriceValue(item?.price || item?.price_text || item?.price_value);
    const rooms = parseNumber(item?.rooms || item?.room_count);
    const area = getAreaValueFromItem(item);
    const fee = parseFeeValue(getFeeCandidate(item));
    const fingerprintParts = [
        regso,
        Number.isFinite(latLng?.lat) ? latLng.lat.toFixed(6) : '',
        Number.isFinite(latLng?.lng) ? latLng.lng.toFixed(6) : '',
        address,
        price == null ? '' : String(price),
        rooms == null ? '' : String(rooms),
        area == null ? '' : String(area),
        fee == null ? '' : String(fee),
    ].filter(Boolean);

    if (fingerprintParts.length) {
        return `listing:${fingerprintParts.join('|')}`;
    }

    return `listing:${idScope || 'scope'}:${index}`;
};

const normalizeListings = (payload, idScope = '') => payload
    .map((item, index) => {
        const latLng = getLatLng(item);
        const areaSource = getAreaCandidate(item);
        const feeCandidate = getFeeCandidate(item);
        const regsoCode = resolveRegsoCode(item) || null;
        const homeId = getListingHomeId(item);
        const listingUrl = normalizeListingPath(item);
        return ({
        id: buildListingId(item, index, idScope),
        homeId,
        listingUrl,
        fullData: normalizeBoolean(item?.fullData ?? item?.full_data, false),
        regsoId: regsoCode,
        regsoCode,
        regso: regsoCode,
        ...latLng,
        address: item.address || item.title || 'Okänd adress',
        title: item.title || item.address || 'Okänd adress',
        ort: item.ort || '',
        postort: item.postort || item.postOrt || item.post_ort || '',
        kommun: item.kommun || '',
        subtitle: item.subtitle || item.ort || item.kommun || '',
        type: normalizeType(item),
        price: item.price || item.price_text || '',
        fee: normalizeFeeText(feeCandidate),
        area: toAreaDisplay(areaSource),
        areaValue: getAreaValueFromItem(item),
        sqmPrice: item.sqmPrice || item.sqm_price || '',
        rooms: item.rooms || item.room_count || '',
        plot: item.plot || item.plot_area || '',
        days: normalizeDaysLabel(item.days || item.days_on_market || item.date || ''),
        realtorUrl: item.realtorUrl || item.realtor_url || '',
        broker: item.broker || item.broker_name || '',
        brokerLogo: item.brokerLogo || item.broker_logo || '',
        images: normalizeMediaArray(item.images, item.image),
        planritning: normalizeMediaArray(item.planritning, item.floorplan),
        imgTag: item.imgTag || item.img_tag || '',
        betyg: item.betyg ?? item.rating ?? item.totalScore ?? null,
        priceValue: parsePriceValue(item.price || item.price_text || item.price_value),
        feeValue: parseFeeValue(feeCandidate),
        roomsValue: parseNumber(item.rooms || item.room_count),
        sqmPriceValue: parseNumber(item.sqmPrice || item.sqm_price),
        // undefined when the backend doesn't say (older API), so filters let it through.
        isNewConstruction: item.isNewConstruction == null ? undefined : Boolean(item.isNewConstruction),
    })})
    .filter((item) => typeof item.lat === 'number' && typeof item.lng === 'number' && item.lat !== 0 && item.lng !== 0);

const buildAreaSummary = (listings) => {
    let withArea = 0;
    let withoutArea = 0;
    listings.forEach((listing) => {
        if (listing.areaValue != null) withArea += 1;
        else withoutArea += 1;
    });
    return { total: listings.length, withArea, withoutArea };
};

const getAreaKeySnapshot = (item) => {
    if (!item || typeof item !== 'object') return {};
    const snapshot = {};
    Object.keys(item).forEach((key) => {
        if (AREA_KEY_PATTERN.test(key)) {
            snapshot[key] = item[key];
        }
    });
    return snapshot;
};

const extractListingsPayload = (result) => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.listings)) return result.listings;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.results)) return result.results;
    if (Array.isArray(result?.items)) return result.items;

    if (result && typeof result === 'object') {
        const flattened = [];
        Object.values(result).forEach((value) => {
            if (Array.isArray(value)) {
                flattened.push(...value);
                return;
            }
            if (value && typeof value === 'object') {
                if (Array.isArray(value.listings)) flattened.push(...value.listings);
                if (Array.isArray(value.data)) flattened.push(...value.data);
                if (Array.isArray(value.results)) flattened.push(...value.results);
                if (Array.isArray(value.items)) flattened.push(...value.items);
            }
        });
        if (flattened.length) return flattened;
    }

    return [];
};

const chunkArray = (items, chunkSize) => {
    if (!Array.isArray(items) || !items.length) return [];
    const safeChunkSize = Math.max(1, chunkSize || 1);
    const chunks = [];
    for (let i = 0; i < items.length; i += safeChunkSize) {
        chunks.push(items.slice(i, i + safeChunkSize));
    }
    return chunks;
};

const loadRegsoIds = async () => {
    if (regsoIdsPromise) {
        return regsoIdsPromise;
    }
    regsoIdsPromise = (async () => {
        const requestStart = nowMs();
        const response = await fetch(REGSO_IDS_FILE_URL);
        const responseMs = nowMs() - requestStart;
        if (!response.ok) {
            throw new Error(`Failed to load regso ids: ${response.status}`);
        }
        const parseStart = nowMs();
        const text = await response.text();
        const ids = text
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);
        return ids;
    })();
    return regsoIdsPromise;
};

const dedupeListingsById = (listings) => {
    const seen = new Set();
    return listings.filter((listing) => {
        const id = String(listing?.id ?? '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
    });
};

export const normalizeListingsPayload = (result, idScope = '') => {
    const payload = extractListingsPayload(result);
    if (!payload.length) return [];
    return dedupeListingsById(normalizeListings(payload, idScope));
};

const openListingsCacheDb = () => new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
        resolve(null);
        return;
    }
    const request = window.indexedDB.open(LISTINGS_CACHE_DB, 1);
    request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(LISTINGS_CACHE_STORE)) {
            db.createObjectStore(LISTINGS_CACHE_STORE, { keyPath: 'id' });
        }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
});

const readCachedListings = async (endpoint) => {
    const db = await openListingsCacheDb();
    if (!db) return null;
    const cacheId = buildObfuscatedKey(endpoint) || endpoint;
    return new Promise((resolve) => {
        const tx = db.transaction(LISTINGS_CACHE_STORE, 'readonly');
        const store = tx.objectStore(LISTINGS_CACHE_STORE);
        const request = store.get(cacheId);
        request.onsuccess = () => {
            const cached = request.result;
            const decodedListings =
                Array.isArray(cached?.d)
                    ? cached.d
                    : Array.isArray(cached?.listings)
                        ? cached.listings
                        : deobfuscatePayload(cached?.p || cached?.payloadObfuscated, endpoint);
            const savedAt = cached?.ts ?? cached?.savedAt;
            const isValidCache =
                cached &&
                Array.isArray(decodedListings) &&
                typeof savedAt === 'number' &&
                Date.now() - savedAt < LISTINGS_CACHE_MAX_AGE_MS;
            resolve(isValidCache ? decodedListings : null);
        };
        request.onerror = () => resolve(null);
        tx.oncomplete = () => db.close();
    });
};

const writeCachedListings = async (endpoint, listings) => {
    const db = await openListingsCacheDb();
    if (!db) return;
    const cacheId = buildObfuscatedKey(endpoint) || endpoint;
    const obfuscatedData = obfuscatePayload(listings, endpoint);
    await new Promise((resolve) => {
        const tx = db.transaction(LISTINGS_CACHE_STORE, 'readwrite');
        tx.objectStore(LISTINGS_CACHE_STORE).put({
            id: cacheId,
            ts: Date.now(),
            p: obfuscatedData,
            d: obfuscatedData ? undefined : listings,
        });
        tx.oncomplete = resolve;
        tx.onerror = resolve;
        tx.onabort = resolve;
    });
    db.close();
};

const runtimeListingsStore = new Map();

const getRuntimeEntry = (endpoint) => {
    if (!runtimeListingsStore.has(endpoint)) {
        runtimeListingsStore.set(endpoint, {
            listings: [],
            processed: false,
            loading: false,
            loadPromise: null,
            listeners: new Set(),
        });
    }
    return runtimeListingsStore.get(endpoint);
};

const notifyRuntimeEntry = (entry) => {
    entry.listeners.forEach((listener) => {
        listener(entry);
    });
};

const subscribeRuntimeEntry = (endpoint, listener) => {
    const entry = getRuntimeEntry(endpoint);
    entry.listeners.add(listener);
    return () => entry.listeners.delete(listener);
};

// ---------------------------------------------------------------------------------------------
// Compact listings (/listings/active/compact): every active listing as parallel columns, no image
// lists (~1.5 MB gzipped instead of ~8 MB). Fetching, caching and parsing run in a Web Worker
// (compactListings.worker.js, logic in compactListingsCore.js); this thread only turns the columns
// into listing objects, in chunks that yield to the browser, and publishes them once. The objects
// have the same fields as normalizeListings() output; the full view fetches images etc. per listing.
const COMPACT_ENDPOINT_PATH = '/listings/active/compact';
// Kartvy's listings (Map.js passes it to useActiveListings; prefetchCompactListings fetches the same).
export const COMPACT_LISTINGS_ENDPOINT = `https://api.xn--omrdeskollen-vcb.nu${COMPACT_ENDPOINT_PATH}`;
const COMPACT_LEGACY_ENDPOINT = 'https://api.xn--omrdeskollen-vcb.nu/listings/active';
// A cached copy younger than this is used without asking the server; older ones are shown at once and
// revalidated in the background (a 304 when nothing changed).
const COMPACT_REVALIDATE_AFTER_MS = 10 * 60 * 1000;
const COMPACT_BUILD_CHUNK = 6000;
// The compact payload's "floor" column holds floor + 10, 0 = unknown (COMPACT_FLOOR_OFFSET in compactListingsCore.js).
const COMPACT_FLOOR_OFFSET = 10;
// Indexed by the compact payload's numeric "type" code (TYPE_CODES in listings_compact.py).
const COMPACT_TYPES = ['apartment', 'house', 'rowhouse', 'fritidshus'];

const isCompactEndpoint = (endpoint) => String(endpoint || '').includes(COMPACT_ENDPOINT_PATH);
const thumbnailUrl = (imageId) => (imageId ? `https://bcdn.se/images/cache/${imageId}_1440x0.jpg` : '');
// Same text as normalizeFeeText() ("4 650 kr/mån") without a toLocaleString call per listing.
const formatFee = (fee) => `${String(Math.round(fee)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} kr/mån`;

const buildListingsFromColumns = async ({ count, numbers, address, dicts }) => {
    const dayLabels = new Map();
    const dayLabelFor = (dateCode) => {
        if (!dateCode) return { date: '', days: '' };
        let cached = dayLabels.get(dateCode);
        if (!cached) {
            const text = String(dateCode);
            const date = `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
            cached = { date, days: normalizeDaysLabel(date) };
            dayLabels.set(dateCode, cached);
        }
        return cached;
    };
    const {
        id, kind, lat, lng, regso, type, price, rooms, fee, area, sqm, plot, date, kommun, ort, tag, img, full,
        floor, feat, year,
    } = numbers;
    const listings = new Array(count);
    for (let start = 0; start < count; start += COMPACT_BUILD_CHUNK) {
        const end = Math.min(count, start + COMPACT_BUILD_CHUNK);
        for (let i = start; i < end; i += 1) {
            const regsoCode = dicts.regso[regso[i]] || null;
            const day = dayLabelFor(date[i]);
            const homeId = String(id[i]);
            const ortName = dicts.ort[ort[i]] || '';
            const kommunName = dicts.kommun[kommun[i]] || '';
            const street = address[i] || 'Okänd adress';
            const thumbnail = thumbnailUrl(img[i]);
            const kindName = dicts.kind[kind[i]];
            listings[i] = {
                id: `${kindName}/${homeId}`,
                homeId,
                listingUrl: `/${kindName}/${homeId}`,
                fullData: full[i] === 1,
                regsoId: regsoCode,
                regsoCode,
                regso: regsoCode,
                lat: lat[i],
                lng: lng[i],
                address: street,
                title: street,
                ort: ortName,
                postort: '',
                kommun: kommunName,
                subtitle: ortName || kommunName,
                type: COMPACT_TYPES[type[i]] || 'house',
                price: price[i] || '',
                fee: fee[i] ? formatFee(fee[i]) : '',
                area: area[i] ? String(area[i]) : '',
                areaValue: normalizeAreaNumber(area[i] || null),
                sqmPrice: sqm[i] || '',
                rooms: rooms[i] || '',
                plot: plot[i] || '',
                date: day.date,
                days: day.days,
                realtorUrl: '',
                broker: '',
                brokerLogo: '',
                images: thumbnail ? [thumbnail] : [],
                planritning: [],
                imgTag: dicts.tag[tag[i]] || '',
                betyg: null,
                priceValue: price[i] || null,
                feeValue: fee[i] || null,
                roomsValue: rooms[i] || null,
                sqmPriceValue: sqm[i] || null,
                // From listingExtra: null / false when the listing doesn't say.
                floorValue: floor[i] ? floor[i] - COMPACT_FLOOR_OFFSET : null,
                yearBuilt: year[i] || null,
                hasBalcony: (feat[i] & 1) === 1,
                hasElevator: (feat[i] & 2) === 2,
                isNewConstruction: (feat[i] & 4) === 4,
            };
        }
        if (end < count) await yieldToMainThread();
    }
    return listings;
};

// Runs the loader in a worker; without worker support, the same code runs here.
const runCompactLoader = (endpoint, onColumns) => {
    // From the site's edge copy first, then the API (edgePayloads.js); the cached copy keeps the API's URL as its name.
    const options = {
        endpoint,
        sources: edgeSources(endpoint),
        revalidateAfterMs: COMPACT_REVALIDATE_AFTER_MS,
        maxAgeMs: LISTINGS_CACHE_MAX_AGE_MS,
    };
    let worker = null;
    try {
        worker = typeof Worker !== 'undefined'
            ? new Worker(new URL('./compactListings.worker.js', import.meta.url))
            : null;
    } catch (error) {
        worker = null;
    }
    if (!worker) {
        return import('./compactListingsCore.js').then(({ loadCompactColumns }) => (
            loadCompactColumns({ ...options, onData: onColumns })
        ));
    }
    return new Promise((resolve, reject) => {
        let pending = Promise.resolve();
        worker.onmessage = ({ data }) => {
            if (data?.type === 'data') {
                pending = pending.then(() => onColumns(data.columns, data.source));
            } else if (data?.type === 'done') {
                worker.terminate();
                pending.then(resolve, reject);
            } else if (data?.type === 'error') {
                worker.terminate();
                pending.then(() => reject(new Error(data.message)), reject);
            }
        };
        worker.onerror = (event) => {
            worker.terminate();
            reject(new Error(event?.message || 'compact listings worker failed'));
        };
        worker.postMessage(options);
    });
};

// Kartvy's listings into its cache ahead of time (listvy and its landing, once the page has settled; the
// owner, 2026-11-04: a first switch to kartvy waited 3.7 s for them): fetched and stored in the worker, not read,
// so the main thread does nothing. Once per page load; kartvy waits for one that's under way (loadCompactListings).
let compactPrefetch = null;
export const prefetchCompactListings = () => {
    if (compactPrefetch) return compactPrefetch;
    const options = {
        prefetch: true,
        endpoint: COMPACT_LISTINGS_ENDPOINT,
        sources: edgeSources(COMPACT_LISTINGS_ENDPOINT),
        revalidateAfterMs: COMPACT_REVALIDATE_AFTER_MS,
        maxAgeMs: LISTINGS_CACHE_MAX_AGE_MS,
    };
    compactPrefetch = new Promise((resolve) => {
        let worker = null;
        try {
            worker = typeof Worker !== 'undefined' ? new Worker(new URL('./compactListings.worker.js', import.meta.url)) : null;
        } catch (error) {
            worker = null;
        }
        if (!worker) {
            resolve('no worker');
            return;
        }
        const finish = (result) => {
            worker.terminate();
            resolve(result);
        };
        worker.onmessage = ({ data }) => finish(data?.type === 'done' ? data.result : `error: ${data?.message || ''}`);
        worker.onerror = () => finish('error');
        worker.postMessage(options);
    });
    return compactPrefetch;
};

const loadCompactListings = async (entry, endpoint) => {
    // A prefetch under way fills the cache this reads: wait for it rather than download the same again.
    if (compactPrefetch && endpoint === COMPACT_LISTINGS_ENDPOINT) await compactPrefetch;
    const startedAt = nowMs();
    let reported = false;
    const publish = async (columns, source) => {
        entry.listings = await buildListingsFromColumns(columns);
        entry.processed = true;
        entry.loading = false;
        notifyRuntimeEntry(entry);
        if (!reported) {
            // How long the map waited for listings (first data), and whether they came from the cache.
            reported = true;
            track('perf.listings', { ms: Math.round(nowMs() - startedAt), source: source || 'network', count: columns.count });
        }
    };
    try {
        await runCompactLoader(endpoint, publish);
        return entry.listings;
    } catch (error) {
        if (entry.processed) return entry.listings;  // a cached copy is showing: keep it
        // The backend may not have the compact endpoint yet: fall back to the full list.
        console.warn('Compact listings unavailable, falling back to /listings/active:', error?.message || error);
        const unsubscribe = subscribeRuntimeEntry(COMPACT_LEGACY_ENDPOINT, (legacy) => {
            entry.listings = legacy.listings;
            entry.processed = legacy.processed;
            entry.loading = legacy.loading;
            notifyRuntimeEntry(entry);
            if (legacy.processed) unsubscribe();
        });
        await startRuntimeLoad(COMPACT_LEGACY_ENDPOINT);
        return entry.listings;
    }
};

const startRuntimeLoad = (endpoint) => {
    const entry = getRuntimeEntry(endpoint);
    if (entry.loading || entry.processed || entry.loadPromise) {
        return entry.loadPromise;
    }
    const runtimeLoadStart = nowMs();
    entry.loading = true;
    entry.processed = false;
    notifyRuntimeEntry(entry);

    if (isCompactEndpoint(endpoint)) {
        entry.loadPromise = loadCompactListings(entry, endpoint).finally(() => {
            entry.loadPromise = null;
        });
        return entry.loadPromise;
    }

    entry.loadPromise = (async () => {
        const cacheReadStart = nowMs();
        const cached = await readCachedListings(endpoint);
        if (cached) {
            const normalizeCachedStart = nowMs();
            const normalizedCached = normalizeCachedListings(cached);
            if (LISTING_AREA_DEBUG) {
            }
            entry.listings = normalizedCached;
            entry.processed = true;
            entry.loading = false;
            notifyRuntimeEntry(entry);
            return normalizedCached;
        }

        try {
            const isBatchEndpoint = endpoint.includes('/regso/batch');

            if (!isBatchEndpoint) {
                const requestStart = nowMs();
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                });
                const responseMs = nowMs() - requestStart;
                if (!response.ok) {
                    throw new Error(`Failed to fetch active listings: ${response.status}`);
                }

                const parseStart = nowMs();
                const result = await response.json();
                const parseMs = nowMs() - parseStart;
                const payload = extractListingsPayload(result);

                const aggregated = [];
                const seenIds = new Set();
                let processedPayload = 0;
                let chunkIndex = 0;
                const totalPayload = payload.length;
                const flushListings = () => {
                    entry.listings = aggregated.slice();
                    notifyRuntimeEntry(entry);
                };

                while (processedPayload < totalPayload) {
                    const nextPayloadIndex = Math.min(processedPayload + LISTINGS_NORMALIZE_CHUNK_SIZE, totalPayload);
                    const rawChunk = payload.slice(processedPayload, nextPayloadIndex);
                    const chunkStart = nowMs();
                    const normalizedChunk = normalizeListings(rawChunk, `all-${chunkIndex}`);
                    const aggregatedBefore = aggregated.length;
                    for (let i = 0; i < normalizedChunk.length; i += 1) {
                        const listing = normalizedChunk[i];
                        const id = String(listing.id);
                        if (!id || seenIds.has(id)) continue;
                        seenIds.add(id);
                        aggregated.push(listing);
                    }
                    processedPayload = nextPayloadIndex;
                    chunkIndex += 1;
                    const shouldFlush = aggregated.length > 0
                        && (processedPayload >= totalPayload || (chunkIndex % LISTINGS_FLUSH_CHUNK_INTERVAL) === 0);
                    if (shouldFlush) {
                        flushListings();
                    }
                    if (processedPayload < totalPayload) {
                        await yieldToMainThread();
                    }
                }

                const dedupeStart = nowMs();
                const normalized = dedupeListingsById(aggregated);
                const dedupeMs = nowMs() - dedupeStart;

                entry.listings = normalized;
                entry.processed = true;
                entry.loading = false;
                notifyRuntimeEntry(entry);
                const cacheWriteStart = nowMs();
                writeCachedListings(endpoint, normalized)
                    .then(() => {
                    })
                    .catch((cacheError) => {
                    });
                return normalized;
            }

            const regsoIdsLoadStart = nowMs();
            const regsoIds = await loadRegsoIds();
            const regsoBatches = chunkArray(regsoIds, REGSO_BATCH_SIZE);
            const aggregated = [];
            const seenIds = new Set();
            let rawPayloadCount = 0;
            let completedRegsoCount = 0;
            let completedBatchCount = 0;
            let lastFlushAt = 0;
            let firstItemAreaKeys = null;
            const totalRegso = regsoIds.length;
            const totalBatches = regsoBatches.length;

            if (!totalRegso) {
                entry.listings = [];
                entry.processed = true;
                entry.loading = false;
                notifyRuntimeEntry(entry);
                return [];
            }

            const flushListings = () => {
                entry.listings = aggregated.slice();
                notifyRuntimeEntry(entry);
            };

            let nextIndex = 0;
            const workerCount = Math.min(MAX_PARALLEL_REGSO_REQUESTS, totalBatches);

            const processRegsoBatch = async (index) => {
                const batchIds = regsoBatches[index] || [];
                if (!batchIds.length) return;
                const batchLabel = `${index + 1}/${totalBatches}`;
                const requestStart = nowMs();
                try {
                    const response = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            regsoIds: batchIds,
                            regso_ids: batchIds,
                            ids: batchIds.join(','),
                            regsoIdsCsv: batchIds.join(','),
                        }),
                    });
                    const responseMs = nowMs() - requestStart;
                    if (!response.ok) {
                        if (LISTING_AREA_DEBUG) {
                            console.warn(`[listing-area-debug] regso batch fetch failed ${index + 1}/${totalBatches}`, {
                                batchIds,
                                status: response.status,
                            });
                        }
                        return;
                    }
                    const jsonParseStart = nowMs();
                    const result = await response.json();
                    const jsonParseMs = nowMs() - jsonParseStart;
                    const payload = extractListingsPayload(result);
                    if (payload.length && firstItemAreaKeys == null) {
                        firstItemAreaKeys = getAreaKeySnapshot(payload[0]);
                    }
                    if (payload.length) {
                        rawPayloadCount += payload.length;
                        const normalizeStart = nowMs();
                        const normalizedChunk = normalizeListings(payload, `batch-${index}`);
                        const normalizeMs = nowMs() - normalizeStart;
                        const aggregatedBefore = aggregated.length;
                        for (let j = 0; j < normalizedChunk.length; j += 1) {
                            const listing = normalizedChunk[j];
                            const id = String(listing.id);
                            if (!id || seenIds.has(id)) continue;
                            seenIds.add(id);
                            aggregated.push(listing);
                        }
                    } else {
                    }
                } catch (regsoError) {
                    if (LISTING_AREA_DEBUG) {
                        console.warn(`[listing-area-debug] regso batch fetch error ${index + 1}/${totalBatches}`, {
                            batchIds,
                            error: regsoError?.message || regsoError,
                        });
                    }
                } finally {
                    completedBatchCount += 1;
                    completedRegsoCount += batchIds.length;
                    const shouldFlush = aggregated.length > 0
                        && (
                            completedRegsoCount >= totalRegso
                            || completedRegsoCount <= (REGSO_BATCH_SIZE * 2)
                            || (completedRegsoCount - lastFlushAt) >= STREAM_FLUSH_REGSO_INTERVAL
                        );
                    if (shouldFlush) {
                        flushListings();
                        lastFlushAt = completedRegsoCount;
                    }
                    if (LISTING_AREA_DEBUG && ((completedBatchCount % 20) === 0 || completedRegsoCount >= totalRegso)) {
                    }
                }
            };

            const runWorker = async () => {
                while (true) {
                    const index = nextIndex;
                    nextIndex += 1;
                    if (index >= totalBatches) return;
                    await processRegsoBatch(index);
                }
            };

            await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

            const dedupeStart = nowMs();
            const normalized = dedupeListingsById(aggregated);
            const dedupeMs = nowMs() - dedupeStart;
            if (LISTING_AREA_DEBUG) {
            }
            entry.listings = normalized;
            entry.processed = true;
            entry.loading = false;
            notifyRuntimeEntry(entry);
            const cacheWriteStart = nowMs();
            writeCachedListings(endpoint, normalized)
                .then(() => {
                })
                .catch((cacheError) => {
                });
            return normalized;
        } catch (error) {
            console.error('Error fetching active listings:', error);
            entry.processed = true;
            entry.loading = false;
            notifyRuntimeEntry(entry);
            return entry.listings;
        } finally {
            entry.loadPromise = null;
        }
    })();

    return entry.loadPromise;
};

const useActiveListings = ({ endpoint = DEFAULT_ENDPOINT, disabled = false, streamResults = true }) => {
    const [listings, setListings] = useState([]);
    const [listingsProcessed, setListingsProcessed] = useState(false);

    useEffect(() => {
        if (disabled) {
            setListings([]);
            setListingsProcessed(true);
            return;
        }

        let active = true;
        const applyEntry = (entry) => {
            if (!active) return;
            if (streamResults || entry.processed) {
                setListings(entry.listings);
            } else {
                setListings([]);
            }
            setListingsProcessed(entry.processed);
        };

        const entry = getRuntimeEntry(endpoint);
        applyEntry(entry);
        const unsubscribe = subscribeRuntimeEntry(endpoint, applyEntry);
        startRuntimeLoad(endpoint);

        return () => {
            active = false;
            unsubscribe();
        };
    }, [endpoint, disabled, streamResults]);

    return { listings, listingsProcessed };
};

export default useActiveListings;
